import { Flex, Item, ListBox, ProgressCircle, View } from "@adobe/react-spectrum";
import { ChevronDown, ChevronUp, CircleFill } from "react-bootstrap-icons";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import styles from "./paymentStatusFilter.module.css";
import { truncateText } from "../../new-message/utils/helpers";

interface IInvoiceStatusFilterProps {
  createParams: (filterType: any, filterValue: any) => void;
  filterParams?: any;
  invoiceStatusList?: any[];
  baseType?: string;
}

const InvoiceStatusFilter: React.FC<IInvoiceStatusFilterProps> = ({
  createParams,
  filterParams,
  invoiceStatusList = [],
  baseType,
}) => {
  const { t } = useTranslation("invoice");
  const _t = (key: string) => t(`invoice_filters.${key}`, { ns: "invoice" });
  const _lt = (key: string, defaultValue: string) =>
    t(key, { ns: "layout_components", defaultValue });
  const [toggleListVisibility, setToggleListVisibility] = useState<any>({ stateList: true });
  const [showLoader] = useState<boolean>(false);

  const statusItems = useMemo(() => {
    const raw = Array.isArray(invoiceStatusList) ? invoiceStatusList : [];
    const normalized = raw
      .filter((x: any) => !!x?.id)
      .filter((x: any) => {
        // If backend provides type/status relation in `status`, filter by current baseType.
        if (!baseType || !x?.status) return true;
        return String(x.status) === String(baseType);
      })
      .map((x: any) => ({
        ident: String(x.id),
        title: x.name || String(x.id),
        color: x.color || "#6c757d",
      }));

    return normalized;
  }, [invoiceStatusList, baseType]);

  const selectedState = filterParams?.FILTER?.COL?.find((col: any) => col.name === "state")?.value;
  const [stateSelectedKey, setStateSelectedKey] = useState<Set<string>>(
    selectedState ? new Set([String(selectedState)]) : new Set()
  );

  const handleSelectionChange = (value: any) => {
    const selected = Array.from(value)[0];
    const key = selected ? String(selected) : "";
    setStateSelectedKey(new Set([key]));
    createParams("state", new Set([key]));
  };

  if (showLoader) {
    return (
      <Flex width="100%" height="100%" UNSAFE_style={{ backgroundColor: "white" }} justifyContent={"center"} alignContent={"center"} alignItems={"center"}>
        <ProgressCircle aria-label={_lt("loading", "Loading...")} isIndeterminate />
      </Flex>
    );
  }

  return (
    <Flex direction={"column"} minWidth={"200px"} maxHeight={"300px"} alignSelf={"stretch"} alignItems={"start"} justifyContent={"start"} UNSAFE_style={{ overflowY: "auto" }}>
      <Flex direction={"column"} gap={"4px"} alignSelf={"stretch"} alignItems={"start"} justifyContent={"start"} UNSAFE_className={styles.standard_status_container}>
        <Flex direction={"row"} height={"36px"} alignSelf={"stretch"} alignItems={"center"} justifyContent={"space-between"} UNSAFE_className={styles.standard_status_heading}>
          <Flex direction={"row"} flex={1} alignItems={"center"} justifyContent={"start"}>
            <View position={"relative"} UNSAFE_className={styles.standard_status_heading_text}>
              {_t("status")}
            </View>
          </Flex>
          {toggleListVisibility.stateList ? (
            <ChevronDown color="#B0BEC5" onClick={() => setToggleListVisibility((preValue: any) => ({ ...preValue, stateList: false }))} style={{ cursor: "pointer" }} />
          ) : (
            <ChevronUp onClick={() => setToggleListVisibility((preValue: any) => ({ ...preValue, stateList: true }))} style={{ cursor: "pointer" }} />
          )}
        </Flex>
        <Flex
          direction={"column"}
          alignSelf={"stretch"}
          alignItems={"start"}
          justifyContent={"start"}
          UNSAFE_className={styles.standard_status_component}
          UNSAFE_style={{ display: toggleListVisibility.stateList ? "" : "none" }}
        >
          <ListBox
            id="listInvoiceStatus"
            width="100%"
            selectionMode="single"
            defaultSelectedKeys={stateSelectedKey}
            selectedKeys={stateSelectedKey}
            items={statusItems}
            onSelectionChange={handleSelectionChange}
            UNSAFE_style={{ color: "#455a64 !important" }}
            aria-label={_t("status")}
          >
            {(item: any) => (
              <Item key={item.ident} textValue={item.title}>
                <Flex direction={"row"} height={"30px"} UNSAFE_style={{ paddingBottom: "30px" }}>
                  <View UNSAFE_style={{ paddingTop: "4px", paddingLeft: "4px" }}>
                    <CircleFill style={{ paddingRight: "10px" }} size={"18px"} color={item.color} />
                  </View>
                  <View UNSAFE_style={{ whiteSpace: "nowrap", color: "#455a64 !important", paddingTop: "5px" }}>
                    <span title={item.title}>{truncateText(item.title, 18)}</span>
                  </View>
                </Flex>
              </Item>
            )}
          </ListBox>
        </Flex>
      </Flex>
    </Flex>
  );
};

export default InvoiceStatusFilter;
